/**
 * Synthesized sound cues for Mara's idle clips — an owl at the window, a
 * raven, and the rumble of a distant mishap. WebAudio on the session's
 * existing context, no sample files; volumes are set dressing, not features.
 */

const CUE_GAIN = 0.16;

function bus(context: AudioContext, pan: number): AudioNode {
  const gain = context.createGain();
  gain.gain.value = CUE_GAIN;
  const panner = context.createStereoPanner();
  panner.pan.value = pan;
  gain.connect(panner).connect(context.destination);
  return gain;
}

/** One soft owl hoot: a sine with a breathy attack and a falling tail. */
function hoot(context: AudioContext, dest: AudioNode, at: number, lengthS: number): void {
  const osc = context.createOscillator();
  const gain = context.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(355, at);
  osc.frequency.linearRampToValueAtTime(330, at + lengthS * 0.35);
  osc.frequency.linearRampToValueAtTime(285, at + lengthS);
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(1, at + 0.05);
  gain.gain.setValueAtTime(1, at + lengthS * 0.6);
  gain.gain.exponentialRampToValueAtTime(0.001, at + lengthS);
  osc.connect(gain).connect(dest);
  osc.start(at);
  osc.stop(at + lengthS + 0.05);
}

/** "hoo... hoo-hoo" from off right, where the window is. */
export function playOwl(context: AudioContext): void {
  const dest = bus(context, 0.55);
  const t0 = context.currentTime + 0.03;
  hoot(context, dest, t0, 0.42);
  hoot(context, dest, t0 + 0.62, 0.22);
  hoot(context, dest, t0 + 0.88, 0.38);
}

/** One harsh caw: a sawtooth swept down with amplitude roughness. */
function caw(context: AudioContext, dest: AudioNode, at: number): void {
  const osc = context.createOscillator();
  const filter = context.createBiquadFilter();
  const gain = context.createGain();
  const rough = context.createOscillator();
  const roughGain = context.createGain();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(640, at);
  osc.frequency.exponentialRampToValueAtTime(390, at + 0.24);
  filter.type = 'bandpass';
  filter.frequency.value = 1500;
  filter.Q.value = 1.1;
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(0.9, at + 0.02);
  gain.gain.setValueAtTime(0.9, at + 0.17);
  gain.gain.exponentialRampToValueAtTime(0.001, at + 0.26);
  // Amplitude roughness gives the caw its rasp.
  rough.type = 'square';
  rough.frequency.value = 42;
  roughGain.gain.value = 0.45;
  rough.connect(roughGain).connect(gain.gain);
  osc.connect(filter).connect(gain).connect(dest);
  osc.start(at);
  osc.stop(at + 0.3);
  rough.start(at);
  rough.stop(at + 0.3);
}

/** Two caws from off right. */
export function playRaven(context: AudioContext): void {
  const dest = bus(context, 0.5);
  const t0 = context.currentTime + 0.03;
  caw(context, dest, t0);
  caw(context, dest, t0 + 0.42);
}

/** A distant muffled explosion: a sub-sine thump under a lowpassed noise burst. */
export function playRumble(context: AudioContext): void {
  const dest = bus(context, -0.35);
  const t0 = context.currentTime + 0.02;
  const thump = context.createOscillator();
  const thumpGain = context.createGain();
  thump.type = 'sine';
  thump.frequency.setValueAtTime(88, t0);
  thump.frequency.exponentialRampToValueAtTime(34, t0 + 0.7);
  thumpGain.gain.setValueAtTime(0, t0);
  thumpGain.gain.linearRampToValueAtTime(1.4, t0 + 0.02);
  thumpGain.gain.exponentialRampToValueAtTime(0.001, t0 + 1.1);
  thump.connect(thumpGain).connect(dest);
  thump.start(t0);
  thump.stop(t0 + 1.2);

  const seconds = 1.4;
  const noise = context.createBuffer(1, Math.ceil(context.sampleRate * seconds), context.sampleRate);
  const samples = noise.getChannelData(0);
  for (let i = 0; i < samples.length; i += 1) samples[i] = Math.random() * 2 - 1;
  const src = context.createBufferSource();
  const filter = context.createBiquadFilter();
  const noiseGain = context.createGain();
  src.buffer = noise;
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(420, t0);
  filter.frequency.exponentialRampToValueAtTime(90, t0 + seconds);
  noiseGain.gain.setValueAtTime(0, t0);
  noiseGain.gain.linearRampToValueAtTime(0.8, t0 + 0.04);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, t0 + seconds);
  src.connect(filter).connect(noiseGain).connect(dest);
  src.start(t0);
}
